import React, { useState, useContext, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { redirectToLogin } from '@redpanda/auth-utils';
import { PUBLIC_PATHS } from '../constants/publicPaths';
import {
  Squares2X2Icon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  WrenchScrewdriverIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  DevicePhoneMobileIcon,
  CheckIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline';
import { UserContext } from '../context/UserContext';
import { useTranslation } from 'react-i18next';
import { Language } from '@redpanda/types';
import { SidebarAccountHeader } from '@redpanda/ui';
import ThemeSelector from './ThemeSelector';

const langOptions: { value: Language; label: string }[] = [
  { value: 'zh', label: '繁體中文' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
];

interface SidebarProps {
  className?: string;
  isMobileOpen?: boolean;
  onCloseMobile?: () => void;
}

interface MenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface MenuItemConfig {
  path: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  publicAccess: boolean; // true = 未登入也可見，false = 需要登入
}

// 單一清單管理所有選單項目，新增功能只需在此加一筆
const allMenuItemConfigs: MenuItemConfig[] = [
  { path: '/',               labelKey: 'navbar.subscription', icon: Squares2X2Icon,          publicAccess: true  },
  { path: '/enterprise',    labelKey: 'navbar.enterprise',   icon: BuildingOffice2Icon,      publicAccess: true  },
  { path: '/store',         labelKey: 'navbar.store',        icon: BuildingStorefrontIcon,   publicAccess: true  },
  { path: '/apps',          labelKey: 'navbar.apps',         icon: DevicePhoneMobileIcon,    publicAccess: true  },
  { path: '/games',         labelKey: 'navbar.games',        icon: PuzzlePieceIcon,          publicAccess: true  },
  { path: '/custom-service', labelKey: 'navbar.outsourcing', icon: WrenchScrewdriverIcon,        publicAccess: true  },
  { path: '/board',          labelKey: 'navbar.board',       icon: ChatBubbleLeftRightIcon,      publicAccess: true  },
  { path: '/wishlist',       labelKey: 'navbar.wishlist',    icon: LightBulbIcon,                publicAccess: true  },
  { path: '/billing-history', labelKey: 'navbar.billing',   icon: CreditCardIcon,               publicAccess: false },
  { path: '/help',          labelKey: 'pages.help.title',   icon: QuestionMarkCircleIcon,   publicAccess: false },
];

const Sidebar: React.FC<SidebarProps> = ({ className = '', isMobileOpen = false, onCloseMobile }) => {
  const location = useLocation();
  const { user, logout, updateUserLanguage, isAuthenticated } = useContext(UserContext)!;
  const { t, i18n } = useTranslation();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 依登入狀態篩選選單項目（已登入顯示全部，未登入只顯示 publicAccess: true）
  const menuItems: MenuItem[] = allMenuItemConfigs
    .filter(config => isAuthenticated || config.publicAccess)
    .map(config => ({
      path: config.path,
      label: t(config.labelKey),
      icon: config.icon,
    }));

  const handleLinkClick = (e?: React.MouseEvent) => {
    // 如果設定選單是打開的，先關閉選單並阻止導航
    if (isSettingsOpen && e) {
      e.preventDefault();
      setIsSettingsOpen(false);
      return;
    }

    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  const changeLanguage = async (lng: string) => {
    if (updateUserLanguage) {
      await updateUserLanguage(lng as Language);
    } else {
      i18n.changeLanguage(lng);
    }
  };

  const isActiveLink = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* 手機版遮罩 */}
      {isMobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onCloseMobile}
        />
      )}

      {/* 設定選單遮罩 - 點擊外部關閉 */}
      {isSettingsOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          onClick={() => setIsSettingsOpen(false)}
        />
      )}

      {/* Sidebar 本體：與 @redpanda/ui AppSidebar 同色（深色時與內容區同色或更暗＋右側細線，§3-9） */}
      <div className={`bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 ${className} ${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      } transition-transform duration-300 ease-in-out`}>
        {/* Logo 區域 */}
        <div className="p-6">
          <Link to="/" onClick={handleLinkClick} className="inline-block hover:opacity-80 transition-opacity duration-200">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">RedPanda Tech</h1>
          </Link>
        </div>

      {/* 導覽選單 */}
      <nav className="mt-4">
        {/* 項目之間不留空隙：間距放在連結內（py-0.5），滑鼠在項目間移動時游標不會在手指／箭頭間來回切換（Windows 會畫出半截游標） */}
        <ul className="px-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActiveLink(item.path);

            return (
              <li key={item.path}>
                <Link to={item.path} onClick={handleLinkClick} className="group block py-0.5">
                  <span
                    className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors duration-200 ${
                      isActive
                        ? 'bg-brand-50 text-brand-700 dark:bg-brand-950/50 dark:text-brand-200'
                        : 'text-slate-700 group-hover:bg-slate-100 dark:text-gray-300 dark:group-hover:bg-slate-800'
                    }`}
                  >
                    {/* 選單顏色同 @redpanda/ui AppSidebar */}
                    <Icon className={`h-5 w-5 ${isActive ? 'text-brand-600 dark:text-brand-400' : 'text-slate-400 dark:text-gray-400'}`} />
                    <span className="font-medium">{item.label}</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* 設定區域 */}
      <div className="absolute bottom-3 left-0 right-0 p-3 bg-white dark:bg-slate-950 border-t border-slate-100 dark:border-slate-800">
        {isAuthenticated ? (
          /* 已登入：完整設定齒輪選單 */
          <div className="relative">
            <button
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-gray-300 dark:hover:bg-slate-800 transition-colors duration-200 w-full"
            >
              <Cog6ToothIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <span className="font-medium">{t('common.navigation.settings')}</span>
            </button>

            {/* 設定選單 */}
            {isSettingsOpen && (
              <div className="absolute bottom-full left-2 mb-2 w-60 bg-[var(--surface-raised)] rounded-lg [box-shadow:var(--card-shadow-hover)] border border-[var(--border-c)] z-[60] p-2">
                {/* 目前登入的帳號（共用 @redpanda/ui，同各系統側欄） */}
                {(user?.name || user?.email) && (
                  <>
                    <SidebarAccountHeader name={user.name} email={user.email} />
                    <div className="border-t border-[var(--border-c)] mb-2"></div>
                  </>
                )}

                {/* 個人資訊 */}
                <Link
                  to="/profile"
                  onClick={() => {
                    setIsSettingsOpen(false);
                    handleLinkClick();
                  }}
                  className="block px-3 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--page-bg)] font-medium rounded-lg transition-colors duration-150"
                >
                  {t('common.navigation.profile')}
                </Link>

                {/* 管理員選項 */}
                {user?.role === 'SUPER_ADMIN' && (
                  <Link
                    to="/admin"
                    onClick={() => {
                      setIsSettingsOpen(false);
                      handleLinkClick();
                    }}
                    className="block px-3 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--page-bg)] font-medium rounded-lg transition-colors duration-150"
                  >
                    {t('common.navigation.admin')}
                  </Link>
                )}

                <div className="border-t border-[var(--border-c)] my-2"></div>

                {/* 主題切換 */}
                <div className="px-3 py-2">
                  <span className="block text-[13px] font-medium text-[var(--text-muted)] mb-1.5">{t('common.navigation.theme')}</span>
                  <ThemeSelector />
                </div>

                {/* 語言選擇 */}
                <div className="px-3 py-2">
                  <span className="block text-[13px] font-medium text-[var(--text-muted)] mb-1.5">{t('common.navigation.language')}</span>
                  <div className="relative" ref={langRef}>
                    <button
                      onClick={() => setLangOpen(v => !v)}
                      className="w-full flex items-center gap-2 bg-[var(--surface-raised-2)] border border-[var(--border-c)] hover:brightness-110 rounded-md px-3 h-9 text-sm text-[var(--text-secondary)] transition-colors"
                    >
                      <span className="flex-1 text-left">
                        {langOptions.find(o => o.value === i18n.language)?.label ?? i18n.language}
                      </span>
                      <svg className={`h-4 w-4 transition-transform ${langOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {langOpen && (
                      <div className="absolute bottom-full left-0 mb-1.5 w-full bg-[var(--surface-raised-2)] rounded-md [box-shadow:var(--card-shadow-hover)] border-2 border-[var(--border-c)] z-[70] overflow-hidden py-1">
                        {langOptions.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => { changeLanguage(opt.value); setLangOpen(false); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-[var(--page-bg)] transition-colors text-[var(--text-secondary)] border-b border-[var(--border-c)] last:border-b-0"
                          >
                            <span className="flex-1 text-left">{opt.label}</span>
                            {opt.value === i18n.language && (
                              <CheckIcon className="h-3 w-3 text-[var(--text-muted)]" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t border-[var(--border-c)] my-2"></div>

                {/* 登出按鈕：公開頁登出後留在當前頁，私密頁登出後導向登入頁 */}
                <button
                  onClick={() => {
                    setIsSettingsOpen(false);
                    handleLinkClick();
                    const isOnPublicPage = PUBLIC_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
                    logout(isOnPublicPage ? window.location.href : undefined);
                  }}
                  className="block w-full text-left px-3 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 font-medium rounded-lg transition-colors duration-150"
                >
                  {t('common.navigation.logout')}
                </button>
              </div>
            )}
          </div>
        ) : (
          /* 未登入：主題、語言 + 登入按鈕 */
          <div className="space-y-2">
            {/* 主題選擇 */}
            <ThemeSelector />

            {/* 語言選擇 */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(v => !v)}
                className="w-full flex items-center gap-2 bg-[var(--surface-raised-2)] border border-[var(--border-c)] hover:brightness-110 rounded-md px-3 h-9 text-sm text-[var(--text-secondary)] transition-colors"
              >
                <span className="flex-1 text-left">
                  {langOptions.find(o => o.value === i18n.language)?.label ?? i18n.language}
                </span>
                <svg className={`h-4 w-4 transition-transform ${langOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {langOpen && (
                <div className="absolute bottom-full left-0 mb-1.5 w-full bg-[var(--surface-raised-2)] rounded-md [box-shadow:var(--card-shadow-hover)] border-2 border-[var(--border-c)] z-[70] overflow-hidden py-1">
                  {langOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { changeLanguage(opt.value); setLangOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-[var(--page-bg)] transition-colors text-[var(--text-secondary)] border-b border-[var(--border-c)] last:border-b-0"
                    >
                      <span className="flex-1 text-left">{opt.label}</span>
                      {opt.value === i18n.language && (
                        <CheckIcon className="h-3 w-3 text-[var(--text-muted)]" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 登入按鈕 */}
            <button
              onClick={() => {
                const identityServiceUrl = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000';
                redirectToLogin(identityServiceUrl, window.location.href);
              }}
              className="flex items-center justify-center space-x-2 w-full px-3 py-2 bg-[var(--accent-fill)] hover:opacity-90 text-[var(--accent-on)] font-semibold rounded-lg transition-opacity duration-150"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              <span>{t('common.navigation.login', '登入')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default Sidebar;
